import { designatedContractorType, operationalRuleType } from "@agentcall/core/type"
import type {
  OperationRule,
  OperationRuleAgentCallContractorApproval,
  OperationRuleArrangementLimit,
  OperationRuleHasDesignatedContractor,
} from "./models"

// 手配上限金額のラベル取得
export function getArrangementLimitLabel(
  limit: OperationRuleArrangementLimit | null | undefined
): string {
  if (!limit) return "-"
  const option = operationalRuleType.ARRANGEMENT_LIMIT_OPTIONS.find((opt) => opt.key === limit)
  return option?.value ?? "-"
}

// AC業者手配のラベル取得
export function getAgentCallContractorApprovalLabel(
  approval: OperationRuleAgentCallContractorApproval | null | undefined
): string {
  if (!approval) return "-"
  const option = operationalRuleType.AGENTCALL_CONSENT_OPTIONS.find((opt) => opt.key === approval)
  return option?.value ?? "-"
}

// 指定業者有無のラベル取得
export function getHasDesignatedContractorLabel(
  status: OperationRuleHasDesignatedContractor | null | undefined
): string {
  if (!status) return "-"
  const option = operationalRuleType.DESIGNATED_CONTRACTOR_OPTIONS.find((opt) => opt.key === status)
  return option?.value ?? "-"
}

// カテゴリのラベル取得
export function getCategoryLabel(category: string): string {
  const option = operationalRuleType.CONTRACTOR_CATEGORIES.find((opt) => opt.key === category)
  return option?.value ?? category
}

// カテゴリ配列からラベル文字列を取得
export function getCategoriesLabel(categories: string[] | null | undefined): string {
  if (!categories || categories.length === 0) return "-"
  return categories.map(getCategoryLabel).join("、")
}

// 依頼方法のラベル取得
export function getRequestMethodLabel(method: string | null | undefined): string {
  if (!method) return "-"
  switch (method) {
    case designatedContractorType.REQUEST_METHOD_EMAIL:
      return "メール"
    case designatedContractorType.REQUEST_METHOD_FAX:
      return "FAX"
    case designatedContractorType.REQUEST_METHOD_PHONE:
      return "電話"
    default:
      return method
  }
}

// 一覧表示用に運用ルールを1行サマリにする(E-13)。
export function getOperationRuleSummary(
  rule: Pick<
    OperationRule,
    "agentCallContractorApproval" | "hasDesignatedContractor" | "arrangementLimit"
  >
): string {
  const consent = getAgentCallContractorApprovalLabel(rule.agentCallContractorApproval)
  const designated = getHasDesignatedContractorLabel(rule.hasDesignatedContractor)
  const limit = getArrangementLimitLabel(rule.arrangementLimit)
  return `手配承諾: ${consent} ／ 指定業者: ${designated} ／ 手配上限: ${limit}`
}

/**
 * 物件に適用される運用ルールを「物件固有 → 無ければオーナー既定」で解決する(E-13)。
 * propertyId 未指定(物件が無い案件など)は既定ルールを返す。どちらも無ければ null。
 * 返ったルールの propertyId が null なら既定へフォールバックしたことを意味する。
 */
export function resolveOperationRule(
  rules: ReadonlyArray<OperationRule> | null | undefined,
  propertyId: number | null | undefined
): OperationRule | null {
  if (!rules) return null
  const propertyRule =
    propertyId == null ? undefined : rules.find((rule) => rule.propertyId === propertyId)
  return propertyRule ?? rules.find((rule) => rule.propertyId == null) ?? null
}

// 運用ルール詳細ページの URL。propertyId 無し=オーナー既定、あり=物件固有(E-13)。
export function getOperationRuleDetailPath(ownerId: string, propertyId: number | null): string {
  return `/owners/${ownerId}/operation-rules${propertyId == null ? "" : `?propertyId=${propertyId}`}`
}
